import { useCallback, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import PeopleIcon from '@mui/icons-material/People';
import DeleteIcon from '@mui/icons-material/Delete';
import { EmptyState } from '../components/common/EmptyState';
import { FilterableSelect } from '../components/common/FilterableSelect';
import { useNavigate } from '@tanstack/react-router';
import { EntityCreateDialog } from '../components/entities/EntityCreateDialog';
import {
  ENTITY_TYPES,
  useBatchDeleteEntities,
  useDeleteEntity,
  useEntities,
  type EntitySummaryDto,
  type EntityTypeSlug,
  type LibraryScopeSlug,
} from '../api/queries/entities';
import { AppImage } from '../components/common/AppImage';
import { ConfirmDialog } from '../components/common/ConfirmDialog';

type ViewMode = 'list' | 'grid';
type SortField = 'recently_edited' | 'name' | 'type' | 'most_images';

function getStoredViewMode(): ViewMode {
  try {
    const stored = localStorage.getItem('entities-view-mode');
    if (stored === 'grid' || stored === 'list') return stored;
  } catch {
    /* ignore */
  }
  return 'list';
}

function sortEntities(
  items: EntitySummaryDto[],
  sortBy: SortField,
): EntitySummaryDto[] {
  const sorted = [...items];
  switch (sortBy) {
    case 'name':
      sorted.sort((a, b) => a.name.localeCompare(b.name));
      break;
    case 'type':
      sorted.sort((a, b) => a.entityType.localeCompare(b.entityType));
      break;
    case 'most_images':
      sorted.sort((a, b) => b.imageCount - a.imageCount);
      break;
    case 'recently_edited':
    default:
      sorted.sort(
        (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
      );
      break;
  }
  return sorted;
}

export function EntitiesRoute() {
  const [scope, setScope] = useState<LibraryScopeSlug>('private');
  const [type, setType] = useState<EntityTypeSlug | ''>('');
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>(getStoredViewMode);
  const [sortBy, setSortBy] = useState<SortField>('recently_edited');
  const [deleteTarget, setDeleteTarget] = useState<EntitySummaryDto | null>(
    null,
  );
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [batchDeleteConfirmOpen, setBatchDeleteConfirmOpen] = useState(false);
  const deleteEntity = useDeleteEntity();
  const batchDelete = useBatchDeleteEntities();

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleBatchDelete = () => {
    if (selectedIds.size === 0) return;
    setBatchDeleteConfirmOpen(true);
  };

  const handleBatchDeleteConfirm = async () => {
    await batchDelete.mutateAsync(Array.from(selectedIds));
    setSelectedIds(new Set());
    setBatchDeleteConfirmOpen(false);
  };
  const navigate = useNavigate();

  const query = useEntities({
    scope,
    type: type || undefined,
    search: search.trim() || undefined,
    pageSize: 100,
  });

  const handleViewChange = (mode: ViewMode) => {
    setViewMode(mode);
    try {
      localStorage.setItem('entities-view-mode', mode);
    } catch {
      /* ignore */
    }
  };

  const items = sortEntities(query.data?.items ?? [], sortBy);

  return (
    <div>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ flex: 1 }}>
          Entities
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate({ to: '/entities/new' })}
        >
          New entity
        </Button>
      </Stack>

      <Stack
        direction="row"
        spacing={1}
        sx={{ mb: 3 }}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
      >
        <FilterableSelect
          label="Scope"
          value={scope}
          onChange={(newValue) =>
            setScope((newValue || 'private') as LibraryScopeSlug)
          }
          allowEmpty={false}
          options={[
            { value: 'private', label: 'My library' },
            { value: 'shared', label: 'Shared library' },
          ]}
          sx={{ flex: 1, minWidth: 160 }}
        />
        <FilterableSelect
          label="Type"
          value={type}
          onChange={(newValue) =>
            setType((newValue || '') as EntityTypeSlug | '')
          }
          allowEmpty={true}
          emptyLabel="All types"
          options={ENTITY_TYPES.map((t) => ({
            value: t.value,
            label: t.label,
          }))}
          sx={{ flex: 1, minWidth: 160 }}
        />
        <FilterableSelect
          label="Sort"
          value={sortBy}
          onChange={(newValue) =>
            setSortBy((newValue || 'recently_edited') as SortField)
          }
          allowEmpty={false}
          options={[
            { value: 'recently_edited', label: 'Recently edited' },
            { value: 'name', label: 'Name' },
            { value: 'type', label: 'Type' },
            { value: 'most_images', label: 'Most images' },
          ]}
          sx={{ flex: 1, minWidth: 170 }}
        />
        <TextField
          size="small"
          label="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 150, flexShrink: 0 }}
        />
        <Stack direction="row" spacing={0}>
          <Tooltip title="Grid view">
            <IconButton
              onClick={() => handleViewChange('grid')}
              color={viewMode === 'grid' ? 'primary' : 'default'}
            >
              <ViewModuleIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="List view">
            <IconButton
              onClick={() => handleViewChange('list')}
              color={viewMode === 'list' ? 'primary' : 'default'}
            >
              <ViewListIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {query.isLoading && <CircularProgress />}
      {query.isError && (
        <Alert severity="error">Failed to load entities.</Alert>
      )}

      {viewMode === 'list' ? (
        <Stack spacing={1}>
          {items.map((e) => (
            <Card
              key={e.id}
              sx={{ cursor: 'pointer' }}
              onClick={() =>
                navigate({
                  to: '/entities/$entityId',
                  params: { entityId: e.id },
                })
              }
            >
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Checkbox
                    size="small"
                    checked={selectedIds.has(e.id)}
                    onClick={(ev) => {
                      ev.stopPropagation();
                      toggleSelect(e.id);
                    }}
                  />
                  <AppImage
                    src={
                      e.referenceImageUrl ??
                      e.referenceImageThumbnailUrl ??
                      null
                    }
                    alt={e.name}
                    width={48}
                    height={48}
                    objectFit="cover"
                    sx={{ borderRadius: 1, flexShrink: 0 }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle1" fontWeight={600} noWrap>
                      {e.name}
                    </Typography>
                    {e.description && (
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {e.description.slice(0, 120)}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {e.descriptorCount} descriptor
                      {e.descriptorCount === 1 ? '' : 's'} · {e.imageCount}{' '}
                      image{e.imageCount === 1 ? '' : 's'}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Chip size="small" label={e.entityType} />
                    {e.hasApprovedLora && (
                      <Chip size="small" color="success" label="LoRA" />
                    )}
                    {e.libraryScope === 'shared' && (
                      <Chip size="small" color="info" label="shared" />
                    )}
                  </Stack>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={(ev) => {
                      ev.stopPropagation();
                      setDeleteTarget(e);
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: 2,
          }}
        >
          {items.map((e) => (
            <EntityGridCard
              key={e.id}
              entity={e}
              onClick={() =>
                navigate({
                  to: '/entities/$entityId',
                  params: { entityId: e.id },
                })
              }
              onDelete={() => setDeleteTarget(e)}
            />
          ))}
        </Box>
      )}

      {query.data && items.length === 0 && (
        <EmptyState
          icon={<PeopleIcon sx={{ fontSize: 64 }} />}
          title="Create your first character"
          description="Build detailed character profiles with appearance descriptors, voice profiles, and AI-generated portraits for visual consistency."
          ctaLabel="New Entity"
          onCtaClick={() => setCreateOpen(true)}
          secondaryLabel="Browse archetypes"
          onSecondaryClick={() => navigate({ to: '/libraries/shared' })}
        />
      )}

      <EntityCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(entity) =>
          navigate({
            to: '/entities/$entityId',
            params: { entityId: entity.id },
          })
        }
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Delete entity"
        message={`Are you sure you want to delete "${deleteTarget?.name}"? This action cannot be undone.`}
        onConfirm={async () => {
          if (deleteTarget) {
            await deleteEntity.mutateAsync(deleteTarget.id);
            setDeleteTarget(null);
          }
        }}
        onCancel={() => setDeleteTarget(null)}
      />

      <ConfirmDialog
        open={batchDeleteConfirmOpen}
        title="Delete entities"
        message={`Delete ${selectedIds.size} selected entities? This action cannot be undone.`}
        confirmLabel="Delete"
        confirmColor="error"
        onConfirm={handleBatchDeleteConfirm}
        onCancel={() => setBatchDeleteConfirmOpen(false)}
      />

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            p: 1.5,
            px: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            borderRadius: 2,
            zIndex: 1200,
          }}
        >
          <CheckBoxIcon color="primary" fontSize="small" />
          <Typography variant="body2" fontWeight={600}>
            {selectedIds.size} selected
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            variant="outlined"
            onClick={() => setSelectedIds(new Set())}
            startIcon={<ClearIcon />}
          >
            Clear
          </Button>
          <Button
            size="small"
            variant="contained"
            color="error"
            onClick={handleBatchDelete}
            disabled={batchDelete.isPending}
          >
            {batchDelete.isPending ? 'Deleting...' : 'Delete Selected'}
          </Button>
        </Paper>
      )}
    </div>
  );
}

function EntityGridCard({
  entity,
  onClick,
  onDelete,
}: {
  entity: EntitySummaryDto;
  onClick: () => void;
  onDelete: () => void;
}) {
  const portraitUrl =
    entity.referenceImageUrl ?? entity.referenceImageThumbnailUrl ?? null;

  return (
    <Paper
      variant="outlined"
      sx={{
        overflow: 'hidden',
        cursor: 'pointer',
        position: 'relative',
        transition: 'box-shadow 0.2s',
        '&:hover': {
          boxShadow: 4,
        },
      }}
      onClick={onClick}
    >
      <IconButton
        size="small"
        color="error"
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
      <AppImage
        src={portraitUrl}
        alt={entity.name}
        width="100%"
        height={180}
        objectFit="cover"
        objectPosition="top center"
        sx={{ borderRadius: 0 }}
      />
      <Box sx={{ p: 1.5 }}>
        <Typography variant="subtitle2" noWrap>
          {entity.name}
        </Typography>
        <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
          <Chip size="small" label={entity.entityType} />
          {entity.hasApprovedLora && (
            <Chip size="small" color="success" label="LoRA" />
          )}
        </Stack>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 0.5, display: 'block' }}
        >
          {entity.imageCount} image{entity.imageCount === 1 ? '' : 's'}
        </Typography>
      </Box>
    </Paper>
  );
}
